import { Controller } from "@hotwired/stimulus"

const pendingFailuresByRefreshUrl = new Map()
const refreshUrlsAwaitingFocus = new Set()

const ALLOWED_MIME_TYPES = ["application/pdf", "image/png", "image/jpeg"]

export default class extends Controller {
  static targets = [
    "dropzone", "overlay", "input", "list", "actions", "fileRowTemplate", "uploadFormTemplate", "refreshFormTemplate", "submitButton",
    "announcer"
  ]
  static values = { refreshUrl: String, invalidTypeMessage: String }

  connect() {
    this.filesByRowId = new Map()
    this.restorePendingFailures()
    this.restoreFocus()
  }

  restoreFocus() {
    if (!refreshUrlsAwaitingFocus.delete(this.refreshUrlValue)) return

    const failedSelect = this.listTarget.querySelector("[data-row] [data-upload-target]")
    const focusTarget = failedSelect || this.inputTarget
    focusTarget.focus()
  }

  get pendingFailures() {
    if (!pendingFailuresByRefreshUrl.has(this.refreshUrlValue)) {
      pendingFailuresByRefreshUrl.set(this.refreshUrlValue, new Map())
    }
    return pendingFailuresByRefreshUrl.get(this.refreshUrlValue)
  }

  restorePendingFailures() {
    for (const [rowId, { file, url, httpMethod, message }] of this.pendingFailures) {
      this.addRow(file, rowId)
      const row = this.rowElement(rowId)
      this.assignUploadTarget(row, url, httpMethod)
      this.showError(row, message)
    }
    this.updateVisibility()
  }

  filesSelected(event) {
    this.addFiles(event.target.files)
    event.target.value = null
  }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-indigo-400", "bg-indigo-50")
    this.overlayTarget.classList.remove("hidden")
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove("border-indigo-400", "bg-indigo-50")
    this.overlayTarget.classList.add("hidden")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-indigo-400", "bg-indigo-50")
    this.overlayTarget.classList.add("hidden")
    this.addFiles(event.dataTransfer.files)
  }

  addFiles(fileList) {
    Array.from(fileList).forEach((file) => {
      const rowId = this.addRow(file)
      if (!ALLOWED_MIME_TYPES.includes(file.type)) this.markInvalidType(rowId)
    })
    this.updateVisibility()
  }

  markInvalidType(rowId) {
    const row = this.rowElement(rowId)
    row.dataset.invalidType = "true"
    row.querySelector("[data-upload-target]").disabled = true
    this.showError(row, this.invalidTypeMessageValue)
  }

  addRow(file, rowId = `${Date.now()}-${Math.random().toString(36).slice(2)}`) {
    const fragment = this.fileRowTemplateTarget.content.cloneNode(true)
    const row = fragment.querySelector("[data-row]")
    row.dataset.rowId = rowId
    row.querySelector("[data-filename]").textContent = file.name
    this.labelControls(row, rowId, file.name)
    this.filesByRowId.set(rowId, file)
    this.listTarget.appendChild(fragment)
    return rowId
  }

  labelControls(row, rowId, filename) {
    const select = row.querySelector("[data-upload-target]")
    const removeButton = row.querySelector("[data-remove-row]")
    const error = row.querySelector("[data-error]")
    error.id = `document-upload-error-${rowId}`
    select.setAttribute("aria-label", `${select.dataset.label}: ${filename}`)
    select.setAttribute("aria-describedby", error.id)
    removeButton.setAttribute("aria-label", `${removeButton.dataset.label}: ${filename}`)
  }

  rowElement(rowId) {
    return this.listTarget.querySelector(`[data-row-id="${rowId}"]`)
  }

  assignUploadTarget(row, url, httpMethod) {
    const select = row.querySelector("[data-upload-target]")
    const option = Array.from(select.options).find(
      (candidate) => candidate.value === url && candidate.dataset.httpMethod === httpMethod
    )
    if (option) select.value = option.value
  }

  uploadTargetSelected(event) {
    event.target.closest("[data-row]").querySelector("[data-error]").classList.add("hidden")
    event.target.removeAttribute("aria-invalid")
  }

  removeRow(event) {
    const row = event.target.closest("[data-row]")
    this.pendingFailures.delete(row.dataset.rowId)
    this.filesByRowId.delete(row.dataset.rowId)
    row.remove()
    this.updateVisibility()
  }

  updateVisibility() {
    const rows = Array.from(this.listTarget.querySelectorAll("[data-row]"))
    this.listTarget.classList.toggle("hidden", rows.length === 0)
    const hasUploadableRow = rows.some((row) => row.dataset.invalidType !== "true")
    this.actionsTarget.classList.toggle("hidden", !hasUploadableRow)
  }

  async uploadAll() {
    const rows = Array.from(this.listTarget.querySelectorAll("[data-row]")).filter(
      (row) => row.dataset.invalidType !== "true"
    )
    const rowsWithoutTarget = rows.filter((row) => !row.querySelector("[data-upload-target]").value)
    if (rowsWithoutTarget.length > 0) {
      rowsWithoutTarget.forEach((row) => {
        this.showError(row, row.querySelector("[data-error]").dataset.missingTargetMessage)
      })
      return
    }

    const focusWasInside = this.element.contains(document.activeElement)
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.setAttribute("aria-busy", "true")

    let uploadedCount = 0
    for (const row of rows) {
      const rowId = row.dataset.rowId
      const file = this.filesByRowId.get(rowId)
      const option = row.querySelector("[data-upload-target]").selectedOptions[0]
      const url = option.value
      const httpMethod = option.dataset.httpMethod

      const { success, message } = await this.uploadFile(file, url, httpMethod)
      if (success) {
        uploadedCount += 1
        this.pendingFailures.delete(rowId)
        this.filesByRowId.delete(rowId)
        row.remove()
      } else {
        this.pendingFailures.set(rowId, { file, url, httpMethod, message })
        this.showError(row, message)
      }
    }

    if (uploadedCount > 0) {
      if (focusWasInside) refreshUrlsAwaitingFocus.add(this.refreshUrlValue)
      await this.refreshDocumentsTab(uploadedCount)
    }

    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.removeAttribute("aria-busy")
    this.updateVisibility()
  }

  refreshDocumentsTab(uploadedCount) {
    const form = this.cloneForm(this.refreshFormTemplateTarget)
    form.querySelector("[data-uploaded-count]").value = uploadedCount
    return this.submitForm(form)
  }

  showError(row, message) {
    const errorEl = row.querySelector("[data-error]")
    errorEl.textContent = message || errorEl.dataset.message
    errorEl.classList.remove("hidden")
    row.querySelector("[data-upload-target]").setAttribute("aria-invalid", "true")
    this.announce(`${row.querySelector("[data-filename]").textContent}: ${errorEl.textContent}`)
  }

  announce(message) {
    const current = this.announcerTarget.textContent
    this.announcerTarget.textContent = current ? `${current} ${message}` : message
    clearTimeout(this.announcerReset)
    this.announcerReset = setTimeout(() => { this.announcerTarget.textContent = "" }, 5000)
  }

  uploadFile(file, url, httpMethod) {
    const form = this.cloneForm(this.uploadFormTemplateTarget)
    form.action = url

    const methodInput = form.querySelector("[data-method]")
    if (httpMethod && httpMethod !== "post") {
      methodInput.value = httpMethod
    } else {
      methodInput.remove()
    }

    const transfer = new DataTransfer()
    transfer.items.add(file)
    form.querySelector("[data-file]").files = transfer.files

    return this.submitForm(form)
  }

  cloneForm(template) {
    const form = template.content.firstElementChild.cloneNode(true)
    document.body.appendChild(form)
    return form
  }

  submitForm(form) {
    return new Promise((resolve) => {
      form.addEventListener("turbo:submit-end", async (event) => {
        form.remove()
        const { success, fetchResponse } = event.detail
        resolve({ success, message: success ? null : await this.plainTextMessage(fetchResponse) })
      }, { once: true })
      form.requestSubmit()
    })
  }

  async plainTextMessage(fetchResponse) {
    if (!fetchResponse?.contentType?.startsWith("text/plain")) return null

    return (await fetchResponse.responseText).trim() || null
  }
}
